/** @jsxRuntime classic */
/** @jsx jsx */
import { FC, ReactNode, useEffect, useMemo } from 'react';
import { containerCss } from '../../styles';
import { jsx } from '@emotion/react';
import {
	Appendix,
	BillingInfo,
	Body,
	Details,
	Header,
	Notes,
	Payments,
	ResourceNameTypeEnum,
} from '../../../../molecules/invoicing';
import { EInvoiceVariant, IInvoiceTemplateState } from '../../types';
import { useInvoiceTemplate } from '../../hooks/useInvoiceTemplate';
import { i18n } from '../../../../lib/i18n';
import {
	EInvoiceAction,
	EInvoicingTabs,
	PAYMENT_VARIANTS,
	TInvoiceResourceLinkType,
	TOnResourceClick,
} from '../../../../molecules/invoicing/types';
import { ScheduledPayments } from '../../../../molecules/invoicing/scheduledPayments';
import { PrintHeader } from '../../../../molecules/invoicing/printHeader/PrintHeader';
import { cloneDeep } from 'lodash';
import { useWindowSize } from '../../../../hooks/useWindowSize';
import { en as translation } from '../../../../lib/i18n/en';


const PROGRAM_PURCHASED_RESOURCE_TYPE = [ResourceNameTypeEnum.EVENT, ResourceNameTypeEnum.PROGRAM_SEASON];
const BACKOFFICE_VARIANTS = [EInvoiceVariant.BACKOFFICE, EInvoiceVariant.BACKOFFICE_PREVIEW];
const PRINT_VARIANTS = [EInvoiceVariant.PRINT, EInvoiceVariant.PAYMENT_PRINT];

interface IProps {
	state: IInvoiceTemplateState;
	activeTab: EInvoicingTabs;
	isLoading: boolean;
	setActiveTab?: (v: EInvoicingTabs) => void;
	setLoading: (v: boolean) => void;
	additionalInfoRow?: string; // I think this should be automatic in the component
	variant?: EInvoiceVariant;
	onAction: (action: EInvoiceAction, val?: any) => void;
	children?: ReactNode | ReactNode[];
	key?: string | number;
	organizationTimeZone?: string;
	discountVisibility?: { promoCode: boolean; customDiscount: boolean };
	detailsFooterActions?: ReactNode;
	/** Returns whether a discount may be added to every line item in the (possibly merged) row. */
	canAddDiscountToLineItems?: (lineItemsIds: number[]) => boolean;
	/** Returns whether the discount with the given action id may be removed. */
	canRemoveDiscount?: (actionId: string) => boolean;
	/** Tooltip shown when an invoice-level control is disabled by facility access. */
	actionDisabledReason?: string;
	/** Tooltip shown when a line-item control is disabled by facility access. */
	lineItemActionDisabledReason?: string;
	onResourceClick?: TOnResourceClick;
	linkableResourceTypes?: TInvoiceResourceLinkType[];
}

export const InvoiceTemplate: FC<IProps> = props => {
	const { state, activeTab, variant: originalVariant = EInvoiceVariant.CONSUMER, organizationTimeZone } = props;
	const discountVisibility = props.discountVisibility ?? { promoCode: true, customDiscount: true };
	const paymentVariant = PAYMENT_VARIANTS.includes(originalVariant);
	const { rows, appendix, payments, meta, handleProcessData, setShowVoid, showVoid } =
		useInvoiceTemplate(paymentVariant);

	const { isMobile } = useWindowSize();
	const isPrint = PRINT_VARIANTS.includes(originalVariant);
	const isBackoffice = BACKOFFICE_VARIANTS.includes(originalVariant);
	const variant = isBackoffice ? EInvoiceVariant.BACKOFFICE : originalVariant;
	const headerVariant =
		originalVariant === EInvoiceVariant.BACKOFFICE_PREVIEW ? EInvoiceVariant.BACKOFFICE_PREVIEW : variant;
	const isShowNotes = originalVariant === EInvoiceVariant.BACKOFFICE_PREVIEW || !isBackoffice;

	const labels = i18n.invoice;

	const { invoiceState, immutableState } = useMemo(() => {
		const invoiceState = { ...state?.invoice, meta };
		const immutableState = { ...state, invoice: invoiceState };
		return { invoiceState, immutableState };
	}, [state, meta]);

	const additional = useMemo(() => {
		const scheduledPayments = immutableState?.scheduledPayments ?? [];
		for (const sp of scheduledPayments) {
			if (!sp.originalPlannedDate) {
				sp.originalPlannedDate= sp.plannedDate;
			}
			sp.method = translation.PaymentMethodTypeEnum[sp.paymentType] || translation.PaymentMethodTypeEnum.unknown;
		}
		return {
			rows,
			appendix,
			payments,
			scheduledPayments: immutableState.scheduledPayments,
		};
	}, [rows, appendix, payments, immutableState]);

	const showAppendix = activeTab === EInvoicingTabs.SCHEDULE;

	const showPrintAppendix = useMemo(() => {
		return (
			!paymentVariant &&
			isPrint &&
			additional.appendix.some((a: any[]) => {
				const isReservation = a?.some((li: any) => li?.slots?.length > 0);
				const isProgram = a.some((li: any) =>
					PROGRAM_PURCHASED_RESOURCE_TYPE.includes(li?.lineItem?.purchasedResources?.[0]?.resourceType)
				);
				return isReservation || isProgram;
			})
		);
	}, [variant, additional?.appendix, immutableState]);

	const handleOnAction = (action: EInvoiceAction, val: any) => {
		props.onAction(action, val);
	};

	const handleAddLineItemDiscount = (lineItemsIds: number[]) => {
		handleOnAction(EInvoiceAction.ADD_DISCOUNT, lineItemsIds);
	};

	useEffect(() => {
		handleProcessData(cloneDeep(immutableState), handleAddLineItemDiscount);
	}, [state, showVoid]);

	const showDetails = activeTab === EInvoicingTabs.DETAILS || isPrint;

	return (
		<div key={props.key ?? '1'} css={containerCss(isMobile && isPrint)} data-aid="InvoiceTemplate-invoice-template">
			<PrintHeader
				organization={state.organization}
				invoiceId={
					PAYMENT_VARIANTS.includes(variant) ? immutableState.invoice.payments?.[0]?.id : immutableState.invoice.id
				}
				isPrint={PRINT_VARIANTS.includes(variant)}
				variant={variant}
			/>
			<Header {...props} labels={labels} state={immutableState} additional={additional} variant={headerVariant} />
			{showDetails && (
				<Body>
					<BillingInfo
						{...props}
						labels={labels}
						state={immutableState}
						variant={variant}
						isShowTransactionFee={invoiceState.totalFeesAmount > 0}
						organizationTimeZone={organizationTimeZone}
					/>
					<Details
						{...props}
						labels={labels}
						state={immutableState}
						additional={additional}
						variant={variant}
						showVoid={showVoid}
						setShowVoid={setShowVoid}
						onRemoveDiscount={handleOnAction}
						discountVisibility={discountVisibility}
						detailsFooterActions={props.detailsFooterActions}
						canAddDiscountToLineItems={props.canAddDiscountToLineItems}
						canRemoveDiscount={props.canRemoveDiscount}
						actionDisabledReason={props.actionDisabledReason}
						lineItemActionDisabledReason={props.lineItemActionDisabledReason}
						onResourceClick={props.onResourceClick}
						linkableResourceTypes={props.linkableResourceTypes}
					/>
					<ScheduledPayments
						{...props}
						labels={labels as any}
						state={immutableState}
						additional={additional}
						variant={variant}
					/>
					<Payments
						{...props}
						labels={labels as any}
						state={immutableState}
						additional={additional}
						// Use original variant here for differences between backoffice types
						variant={originalVariant}
						onAction={handleOnAction}
						organizationTimeZone={organizationTimeZone}
					/>
					{isShowNotes && (
						<Notes
							{...props}
							labels={labels}
							state={immutableState}
							variant={variant}
							organizationTimeZone={organizationTimeZone}
						/>
					)}
					{Array.isArray(props.children)
						? props.children.map((child, i) => <div key={i}>{child}</div>)
						: props.children}
					{showPrintAppendix && (
						<Appendix {...props} labels={labels} state={immutableState} additional={additional} variant={variant} />
					)}
				</Body>
			)}
			{showAppendix && (
				<Body>
					<Appendix {...props} labels={labels} state={immutableState} additional={additional} variant={variant} />
				</Body>
			)}
		</div>
	);
};
